<template>
  <div class="sub-channel-management">
    <div class="page-card">
      <div class="search-section">
        <el-form
          :model="searchForm"
          class="search-form"
          label-position="left"
          label-width="96px"
        >
          <div class="search-row">
            <el-form-item
              :label="$t('workbench.subChannel.creditCode')"
              class="search-field"
            >
              <el-input
                v-model="searchForm.creditCode"
                :placeholder="$t('common.pleaseInput')"
                clearable
                class="search-input"
                @keyup.enter="handleSearch"
              />
            </el-form-item>
            <el-form-item
              :label="$t('workbench.subChannel.name')"
              class="search-field"
            >
              <el-input
                v-model="searchForm.channelOrgName"
                :placeholder="$t('common.pleaseInput')"
                clearable
                class="search-input"
                @keyup.enter="handleSearch"
              />
            </el-form-item>
            <el-form-item
              :label="$t('workbench.subChannel.contact')"
              class="search-field"
            >
              <el-input
                v-model="searchForm.linkmanName"
                :placeholder="$t('common.pleaseInput')"
                clearable
                class="search-input"
                @keyup.enter="handleSearch"
              />
            </el-form-item>
            <el-form-item
              :label="$t('workbench.subChannel.contactPhone')"
              class="search-field"
            >
              <el-input
                v-model="searchForm.linkmanPhone"
                :placeholder="$t('common.pleaseInput')"
                clearable
                class="search-input"
                @keyup.enter="handleSearch"
              />
            </el-form-item>
            <el-form-item
              :label="$t('workbench.subChannel.district')"
              class="search-field"
            >
              <el-select
                v-model="searchForm.channelDistrictType"
                :placeholder="$t('common.pleaseSelect')"
                clearable
                class="search-input"
              >
                <el-option
                  v-for="opt in channelDistrictTypeOptions"
                  :key="opt.value"
                  :label="opt.label"
                  :value="opt.value"
                />
              </el-select>
            </el-form-item>

            <div class="search-actions">
              <el-button
                type="primary"
                size="default"
                class="btn-query"
                @click="handleSearch"
              >
                <el-icon><Search /></el-icon>
                {{ $t('common.search') }}
              </el-button>
              <el-button
                size="default"
                class="btn-reset"
                @click="handleReset"
              >
                <el-icon><Refresh /></el-icon>
                {{ $t('common.reset') }}
              </el-button>
            </div>
          </div>
        </el-form>
      </div>

      <div class="action-bar">
        <div class="type-filters">
          <el-radio-group
            v-model="searchForm.channelType"
            size="default"
            @change="handleTypeChange"
          >
            <el-radio-button
              v-for="item in typeFilterOptions"
              :key="item.value === '' ? '__all__' : item.value"
              :label="item.value"
            >
              {{ item.label }}
            </el-radio-button>
          </el-radio-group>
        </div>
        <div class="right-actions">
          <el-button
            type="primary"
            @click="handleAdd"
          >
            {{ $t('workbench.subChannel.add') }}
          </el-button>
        </div>
      </div>

      <div
        class="table-wrap"
        :class="{ 'table-wrap--empty': !loading && !tableData.length }"
      >
        <el-table
          v-loading="loading"
          :data="tableData"
          style="width: 100%"
          class="custom-table"
        >
        <el-table-column
          type="index"
          :label="$t('workbench.serialNumber')"
          width="80"
          align="center"
        />
        <el-table-column
          prop="creditCode"
          :label="$t('workbench.subChannel.creditCode')"
          align="center"
          min-width="180"
        />
        <el-table-column
          prop="channelOrgName"
          :label="$t('workbench.subChannel.name')"
          align="center"
          min-width="180"
        />
        <el-table-column
          prop="channelTypeDesc"
          :label="$t('workbench.subChannel.type')"
          align="center"
          min-width="140"
        />
        <el-table-column
          prop="channelDistrictTypeDesc"
          :label="$t('workbench.subChannel.districtFederation')"
          align="center"
          min-width="120"
        />
        <el-table-column
          prop="linkmanName"
          :label="$t('workbench.subChannel.contact')"
          align="center"
          min-width="120"
        />
        <el-table-column
          prop="linkmanPhone"
          :label="$t('workbench.subChannel.contactPhone')"
          align="center"
          min-width="150"
        />
        <el-table-column
          prop="channelOrgRemarks"
          :label="$t('workbench.subChannel.remark')"
          align="center"
          min-width="120"
          show-overflow-tooltip
        />
        <el-table-column
          :label="$t('common.operation')"
          align="center"
          width="180"
          fixed="right"
        >
          <template #default="{ row }">
            <el-button
              type="primary"
              plain
              size="small"
              @click="handleEdit(row)"
            >
              {{ $t('workbench.subChannel.edit') }}
            </el-button>
            <el-button
              type="danger"
              plain
              size="small"
              @click="handleDelete(row)"
            >
              {{ $t('workbench.subChannel.delete') }}
            </el-button>
          </template>
        </el-table-column>
        <template #empty>
          <EmptyPlaceholder
            v-if="!loading"
            :description="$t('common.noData')"
          />
        </template>
        </el-table>
      </div>

      <div class="pagination-section">
        <el-pagination
          v-model:current-page="pagination.pageNum"
          v-model:page-size="pagination.pageSize"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, prev, pager, next, sizes, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <AddSubChannelDialog
      v-model="addDialogVisible"
      :identity-options="addDialogIdentityOptions"
      :channel-district-type-options="channelDistrictTypeOptions"
      @success="loadData"
    />

    <EditSubChannelDialog
      v-model="editDialogVisible"
      :identity-options="addDialogIdentityOptions"
      :channel-district-type-options="channelDistrictTypeOptions"
      :record="editRecord"
      @success="loadData"
    />
  </div>
</template>

<script>
import { Search, Refresh } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { showEnhancedConfirmDialog } from '@/utils/messageUtils'
import { getGovernanceChannelIdentityTypeList, getChannelDistrictTypeList } from '@/api/dict'
import { pageGovernanceSubChannels, cancelGovernanceSubChannelUnderCurrentParent } from '@/api/enterprise'
import { GOVERNANCE_CHANNEL_IDENTITY_PRIMARY } from '@/enums/ChannelTypeEnum'
import EmptyPlaceholder from '@/components/EmptyPlaceholder.vue'
import AddSubChannelDialog from './AddSubChannelDialog.vue'
import EditSubChannelDialog from './EditSubChannelDialog.vue'

export default {
  name: 'SubChannelManagement',
  components: {
    Search,
    Refresh,
    EmptyPlaceholder,
    AddSubChannelDialog,
    EditSubChannelDialog
  },
  data() {
    return {
      loading: false,
      searchForm: {
        creditCode: '',
        channelOrgName: '',
        linkmanName: '',
        linkmanPhone: '',
        channelType: '', // '' 表示全部
        channelDistrictType: ''
      },
      typeOptions: [{ label: '', value: '' }],
      channelDistrictTypeOptions: [],
      tableData: [],
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      addDialogVisible: false,
      editDialogVisible: false,
      editRecord: null
    }
  },
  computed: {
    /** 弹窗内渠道类型（不含「全部」），来自 governance_channel_identity_type */
    addDialogIdentityOptions() {
      return this.typeOptions.filter(o => o.value !== '')
    },
    typeFilterOptions() {
      return this.typeOptions.map(o =>
        o.value === '' ? { ...o, label: this.$t('workbench.all') } : o
      )
    }
  },
  async mounted() {
    await Promise.all([this.loadChannelIdentityDict(), this.loadChannelDistrictTypeDict()])
    this.loadData()
  },
  methods: {
    unwrapDictOptions(resp) {
      const data = resp?.code === 200 ? (resp.data || resp) : (resp || {})
      const list = Array.isArray(data) ? data : (data.data || data.list || data.rows || [])
      return (Array.isArray(list) ? list : [])
        .map(i => {
          const label = i?.label ?? i?.dictLabel ?? i?.name
          const value = i?.value ?? i?.dictValue ?? i?.code
          return {
            label: label == null ? '' : String(label).trim(),
            value: value == null ? '' : String(value).trim()
          }
        })
        .filter(i => i.label && i.value)
    },
    async loadChannelIdentityDict() {
      try {
        const res = await getGovernanceChannelIdentityTypeList()
        const rows = this.unwrapDictOptions(res).filter(
          o => o.value !== GOVERNANCE_CHANNEL_IDENTITY_PRIMARY
        )
        this.typeOptions = [{ label: this.$t('workbench.all'), value: '' }, ...rows]
      } catch (e) {
        console.error('加载身份类型字典失败:', e)
        this.typeOptions = [{ label: this.$t('workbench.all'), value: '' }]
      }
    },
    async loadChannelDistrictTypeDict() {
      try {
        const res = await getChannelDistrictTypeList()
        this.channelDistrictTypeOptions = this.unwrapDictOptions(res)
      } catch (e) {
        console.error('加载区级渠道地区类型字典失败:', e)
        this.channelDistrictTypeOptions = []
      }
    },
    async loadData() {
      this.loading = true
      try {
        const params = {
          pageNum: this.pagination.pageNum,
          pageSize: this.pagination.pageSize
        }
        const cc = (this.searchForm.creditCode || '').trim()
        const cn = (this.searchForm.channelOrgName || '').trim()
        const ln = (this.searchForm.linkmanName || '').trim()
        const lp = (this.searchForm.linkmanPhone || '').trim()
        if (cc) params.creditCode = cc
        if (cn) params.channelOrgName = cn
        if (ln) params.linkmanName = ln
        if (lp) params.linkmanPhone = lp
        if (this.searchForm.channelType) {
          params.channelIdentityType = this.searchForm.channelType
        }
        if (this.searchForm.channelDistrictType) {
          params.channelDistrictType = this.searchForm.channelDistrictType
        }

        const res = await pageGovernanceSubChannels(params)
        if (res && res.code === 200) {
          const rows = res.rows || []
          this.tableData = rows.map(r => ({
            id: r.id,
            creditCode: r.creditCode,
            channelOrgName: r.channelOrgName || '',
            channelIdentityType: r.channelIdentityType || '',
            channelDistrictType: r.channelDistrictType || '',
            channelTypeDesc: this.resolveChannelIdentityLabel(r.channelIdentityType),
            channelDistrictTypeDesc: this.resolveChannelDistrictTypeLabel(r.channelDistrictType),
            linkmanName: r.linkmanName,
            linkmanPhone: r.linkmanPhone,
            channelOrgRemarks: r.channelOrgRemarks || ''
          }))
          this.pagination.total = Number(res.total) || 0
        } else {
          this.tableData = []
          this.pagination.total = 0
        }
      } catch (e) {
        console.error('加载子渠道列表失败:', e)
        this.tableData = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },
    resolveChannelIdentityLabel(code) {
      if (code == null || code === '') return '—'
      const c = String(code).trim()
      const hit = (this.typeOptions || []).find(o => o.value === c)
      return hit ? hit.label : c
    },
    resolveChannelDistrictTypeLabel(code) {
      if (code == null || code === '') return '—'
      const c = String(code).trim()
      const hit = (this.channelDistrictTypeOptions || []).find(o => o.value === c)
      return hit ? hit.label : c
    },
    handleSearch() {
      this.pagination.pageNum = 1
      this.loadData()
    },
    handleReset() {
      this.searchForm = {
        creditCode: '',
        channelOrgName: '',
        linkmanName: '',
        linkmanPhone: '',
        channelType: this.searchForm.channelType,
        channelDistrictType: ''
      }
      this.handleSearch()
    },
    handleTypeChange() {
      this.handleSearch()
    },
    handleImport() {
      ElMessage.info(this.$t('workbench.subChannel.importStub'))
    },
    handleAdd() {
      this.addDialogVisible = true
    },
    handleEdit(row) {
      if (row.id == null) {
        ElMessage.error(this.$t('workbench.subChannel.missingIdEdit'))
        return
      }
      this.editRecord = {
        id: row.id,
        channelOrgName: row.channelOrgName || '',
        channelIdentityType: row.channelIdentityType || '',
        channelDistrictType: row.channelDistrictType || '',
        channelOrgRemarks: row.channelOrgRemarks != null ? row.channelOrgRemarks : ''
      }
      this.editDialogVisible = true
    },
    async handleDelete(row) {
      try {
        const confirmed = await showEnhancedConfirmDialog(
          this.$t('workbench.subChannel.deleteConfirm', {
            name: row.channelOrgName || this.$t('workbench.subChannel.defaultEnterpriseName')
          }),
          this.$t('common.tips'),
          this.$t('common.confirm'),
          this.$t('common.cancel'),
          { type: 'warning' }
        )
        if (!confirmed) return
        if (row.id == null) {
          ElMessage.error(this.$t('workbench.subChannel.missingIdDelete'))
          return
        }
        const res = await cancelGovernanceSubChannelUnderCurrentParent(row.id)
        if (res && res.code === 200) {
          ElMessage.success(res.msg || this.$t('workbench.subChannel.deleteSuccess'))
          this.loadData()
        }
      } catch (error) {
        console.error('删除失败:', error)
      }
    },
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.pageNum = 1
      this.loadData()
    },
    handlePageChange(val) {
      this.pagination.pageNum = val
      this.loadData()
    }
  }
}
</script>

<style lang="scss" scoped>
.sub-channel-management {
  min-height: 100%;
  box-sizing: border-box;

  .page-card {
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
  }

  .search-section {
    margin-bottom: 16px;
    padding: 0;
    box-sizing: border-box;
  }

  .search-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .search-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px 68px;
  }

  :deep(.search-field.el-form-item) {
    margin-bottom: 0;
    margin-right: 0;
    display: inline-flex;
    align-items: center;
  }

  :deep(.search-field .el-form-item__label) {
    width: 84px;
    padding-right: 12px;
    box-sizing: border-box;
    font-size: 14px;
    font-weight: 500;
    color: #191919;
    text-align: left;
    justify-content: flex-start;
  }

  :deep(.search-field .el-form-item__content) {
    margin-left: 0 !important;
  }

  .search-input {
    width: 280px;
  }

  :deep(.search-field .el-select.search-input) {
    width: 280px;
  }

  .search-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    margin-left: auto;
  }

  .btn-query {
    padding: 0 12px;
    border-radius: 4px;
    font-size: 13px;
    background: #0d62f3 !important;
    border-color: #0d62f3 !important;

    &:hover {
      background: #0b56e0 !important;
      border-color: #0b56e0 !important;
    }

    .el-icon {
      margin-right: 4px;
      font-size: 14px;
    }
  }

  .btn-reset {
    padding: 0 12px;
    border-radius: 4px;
    font-size: 13px;
    color: #191919;
    background: #ffffff !important;
    border: 1px solid #e8e8e8 !important;

    &:hover {
      border-color: #c9cdd4 !important;
      background: #f7f8fa !important;
      color: #191919 !important;
    }

    .el-icon {
      margin-right: 4px;
      font-size: 14px;
      color: #0d62f3;
    }
  }

  .action-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 0 20px;

    .type-filters {
      display: flex;
      align-items: center;
      gap: 16px;

      :deep(.el-radio-group) {
        .el-radio-button {
          .el-radio-button__inner {
            padding: 8px 24px;
          }
          &.is-active .el-radio-button__inner {
            background-color: #0D62F3;
            border-color: #0D62F3;
            color: #fff;
          }
        }
      }
    }

    .right-actions {
      display: flex;
      gap: 12px;
    }
  }

  .custom-table {
    border-radius: 8px;
    border: 1px solid #ebeef5;
    
    :deep(th.el-table__cell) {
      background-color: #f5f7fa !important;
      color: #333;
      font-weight: 600;
      padding: 12px 0;
    }
    
    :deep(td.el-table__cell) {
      padding: 12px 0;
    }

    .el-button {
      border-radius: 4px;
    }
  }

  .pagination-section {
    padding: 32px 0 16px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

@media (max-width: 992px) {
  .sub-channel-management .search-actions {
    width: 100%;
    justify-content: flex-start;
    margin-left: 0;
  }
}

@media (max-width: 768px) {
  .sub-channel-management {
    :deep(.search-field.el-form-item) {
      width: 100%;
      min-width: 0;
      flex-direction: column;
      align-items: stretch;
    }

    :deep(.search-field .el-form-item__label) {
      width: 100% !important;
      padding-right: 0;
      padding-bottom: 6px;
      line-height: 1.4;
    }

    :deep(.search-field .el-form-item__content) {
      width: 100%;
    }

    .search-input,
    :deep(.search-field .el-select.search-input) {
      width: 100%;
      min-width: 0;
    }

    .search-row {
      gap: 12px;
    }

    .search-actions {
      width: 100%;
      margin-left: 0;

      .el-button {
        flex: 1;
        min-width: 0;
      }
    }

    .action-bar {
      flex-direction: column;
      align-items: stretch;
      gap: 12px;
      padding: 16px 0;

      .type-filters {
        width: 100%;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;

        :deep(.el-radio-group) {
          display: inline-flex;
          flex-wrap: nowrap;
          width: max-content;
          max-width: none;

          .el-radio-button .el-radio-button__inner {
            padding: 8px 16px;
            white-space: nowrap;
          }
        }
      }

      .right-actions {
        width: 100%;

        .el-button {
          width: 100%;
        }
      }
    }

    .table-wrap {
      width: 100%;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;

      &--empty {
        overflow-x: hidden;

        .custom-table {
          min-width: 0 !important;
          width: 100% !important;

          :deep(.el-table__body-wrapper) {
            overflow-x: hidden;
          }

          :deep(.el-table__empty-block) {
            width: 100% !important;
            max-width: 100%;
          }
        }
      }
    }

    .custom-table {
      min-width: 1100px;
    }

    .pagination-section {
      padding-top: 20px;
      justify-content: center;

      :deep(.el-pagination) {
        width: 100%;
        flex-wrap: wrap;
        justify-content: center;
        row-gap: 8px;
      }

      :deep(.el-pagination__sizes),
      :deep(.el-pagination__jump) {
        display: none;
      }
    }
  }
}

@media (max-width: 480px) {
  .sub-channel-management {
    .action-bar .type-filters :deep(.el-radio-group .el-radio-button .el-radio-button__inner) {
      padding: 8px 12px;
      font-size: 13px;
    }

    .custom-table {
      min-width: 900px;

      :deep(.el-button) {
        padding: 4px 8px;
        font-size: 12px;
      }
    }

    .pagination-section {
      :deep(.el-pagination__total) {
        display: none;
      }
    }
  }
}
</style>
